import type {
  OnsiteUpsellProductSelection,
  OnsiteUpsellRuleAuthoring,
  UpsellCondition,
} from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";

import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { ComponentAction } from "@src/store/slices/experienceBuilder/component";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/component/selectors/getVariationDraft";
import type { OnsiteUpsellComponent } from "@src/store/slices/experienceBuilder/types";
import {
  closeProductPicker,
  getProductPickerData,
  getProductPickerProducts,
  getProductPickerSelectedProducts,
} from "@src/store/slices/productPicker";
import {
  selectionToCartVariantPairs,
  selectionToProductIds,
  selectionToUpsellProductItems,
} from "@src/store/slices/productPicker/util/upsellSelection";

// Write the picker selection back into the onsite-upsell draft, then close.
// Mirrors `saveOfferProductPicker`: the write lives in the thunk, not the dialog.
export const saveOnsiteUpsellProductPicker = createAsyncThunk<void, void>(
  "productPicker/saveOnsiteUpsellProductPicker",
  (_, { dispatch, getState }) => {
    const state = getState();
    const data = getProductPickerData(state);
    const draft = getVariationDraft(state) as OnsiteUpsellComponent | undefined;
    if (!data || !draft) {
      return;
    }

    const selectedProducts = getProductPickerSelectedProducts(state);
    const allProducts = getProductPickerProducts(state);

    // Plain-spread the updated rule (the panel updates the draft the same way).
    const setRule = (
      ruleIndex: number,
      update: (rule: OnsiteUpsellRuleAuthoring) => OnsiteUpsellRuleAuthoring
    ) =>
      dispatch(
        ComponentAction.setDraft({
          ...draft,
          rules: draft.rules.map((rule, i) =>
            i === ruleIndex ? update(rule) : rule
          ),
        })
      );

    const setCondition = (
      ruleIndex: number,
      conditionIndex: number,
      condition: UpsellCondition
    ) =>
      setRule(ruleIndex, (rule) => ({
        ...rule,
        conditions: rule.conditions.map((c, j) =>
          j === conditionIndex ? condition : c
        ),
      }));

    if (data.type === "onsiteUpsellAction") {
      const products: OnsiteUpsellProductSelection = {
        source: "products",
        products: selectionToUpsellProductItems(selectedProducts, allProducts),
      };
      setRule(data.ruleIndex, (rule) => ({ ...rule, products }));
    } else if (data.type === "onsiteUpsellCartVariants") {
      const existing =
        draft.rules[data.ruleIndex]?.conditions[data.conditionIndex];
      setCondition(data.ruleIndex, data.conditionIndex, {
        type: "variants",
        include: existing?.type === "variants" ? existing.include : true,
        variants: selectionToCartVariantPairs(selectedProducts, allProducts),
      });
    } else if (data.type === "onsiteUpsellPdpProducts") {
      const existing =
        draft.rules[data.ruleIndex]?.conditions[data.conditionIndex];
      setCondition(data.ruleIndex, data.conditionIndex, {
        type: "products",
        include: existing?.type === "products" ? existing.include : true,
        productIds: selectionToProductIds(selectedProducts),
      });
    }

    dispatch(closeProductPicker());
  }
);
